// src/hooks/useSituationFeeder.js
// Feeds the Situation Reconciliation Engine from Firestore at app startup.
// Mount once in App.jsx — keeps the store populated for all screens.

import { useEffect } from 'react';
import { useUserStore } from '@store/userStore';
import { useSituationStore } from '@store/situationStore';

export function useSituationFeeder() {
  const zone  = useUserStore(s => s.zone);
  const store = useSituationStore();

  // Update zone in store when user switches zone
  useEffect(() => {
    if (zone) store.setZone(zone);
  }, [zone]);

  // Subscribe to utility_status and road_incidents at app level
  useEffect(() => {
    const unsubs = [];
    (async () => {
      try {
        const { initFirebase } = await import('@services/storage/firebase');
        const result = await initFirebase();
        const db = result?.db;
        if (!db) return;

        const { collection, doc, onSnapshot, query, limit } =
          await import('firebase/firestore');

        // ── Utility status ────────────────────────────────────────
        unsubs.push(onSnapshot(
          doc(db, 'utility_status', 'entergy'),
          snap => { if (snap.exists()) store.setUtilityStatus(snap.data()); },
          () => {}
        ));

        // ── Road incidents ────────────────────────────────────────
        unsubs.push(onSnapshot(
          query(collection(db, 'road_incidents'), limit(200)),
          snap => { store.setIncidents(snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
          () => {}
        ));

        // ── Active alerts ─────────────────────────────────────────
        unsubs.push(onSnapshot(
          query(collection(db, 'alerts'), limit(50)),
          snap => {
            const active = snap.docs
              .map(d => d.data())
              .filter(a => a.status === 'active');
            store.setAlerts(active);
          },
          () => {}
        ));

      } catch(e) {
        console.warn('[useSituationFeeder] ERROR:', e.code, e.message);
      }
    })();
    return () => unsubs.forEach(u => u?.());
  }, []);
}
